import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { TabView, TabPanel } from 'primereact/tabview'
import DashboardOverview from './DashboardOverview'
import MemberViewer from './MemberViewer'
import PaymentDashboard from './PaymentDashboard'
import { InvoiceManagement } from './InvoiceManagement'
import DatabaseBackup from './DatabaseBackup'
import { LdapUserManagement } from './ldap/LdapUserManagement'
import { LogbookManagement } from './LogbookManagement'

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth()

  // Initialize activeIndex from session storage, default to 0
  const [activeIndex, setActiveIndex] = useState(() => {
    const savedIndex = sessionStorage.getItem('dashboard-active-tab')
    return savedIndex ? parseInt(savedIndex, 10) : 0
  })

  const handleTabChange = (index: number) => {
    setActiveIndex(index)
    sessionStorage.setItem('dashboard-active-tab', index.toString())
  }

  const [heroDismissed, setHeroDismissed] = useState(() => {
    return sessionStorage.getItem('hero-dismissed') === 'true'
  })

  const dismissHero = () => {
    setHeroDismissed(true)
    sessionStorage.setItem('hero-dismissed', 'true')
  }

  const isDashboardTab = activeIndex === 0
  const showHero = !heroDismissed

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b" data-component="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12 sm:h-16">
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900">MemberHub</h1>
              <p className="text-sm text-gray-500 hidden sm:block">Member Management System</p>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500">@{user?.username}</p>
              </div>
              <span className="text-xs text-gray-500 sm:hidden">@{user?.username}</span>
              <Button
                icon="pi pi-sign-out"
                onClick={logout}
                className="p-button-outlined p-button-sm sm:hidden"
                aria-label="Logout"
              />
              <Button
                label="Logout"
                icon="pi pi-sign-out"
                onClick={logout}
                className="p-button-outlined p-button-sm hidden sm:inline-flex"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-2 sm:py-6 px-2 sm:px-6 lg:px-8">
        {/* Hero: closable, hidden on mobile for non-Dashboard tabs */}
        {showHero && (
          <div className={`mb-2 sm:mb-6 ${isDashboardTab ? '' : 'hidden sm:block'}`}>
            <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
              <div className="p-2 sm:p-6 relative">
                <button
                  onClick={dismissHero}
                  className="absolute top-1 right-1 sm:top-2 sm:right-2 text-white/70 hover:text-white transition-colors"
                  aria-label="Dismiss welcome message"
                >
                  <i className="pi pi-times text-sm" />
                </button>
                <h2 className="text-base sm:text-xl font-semibold mb-1">
                  Welcome back, {user?.firstName || user?.lastName || user?.username || 'User'}!
                </h2>
                <p className="text-blue-100 text-xs sm:text-base">
                  Manage your organization's members from this dashboard
                </p>
              </div>
            </Card>
          </div>
        )}

        <div data-component="dashboard-tabs" className="bg-white rounded-lg shadow-sm">
          <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => handleTabChange(e.index)}
            className="w-full"
          >
            <TabPanel
              header="Dashboard"
              leftIcon="pi pi-chart-bar"
            >
              <DashboardOverview
                onNavigateToMembers={() => setActiveIndex(1)}
                onNavigateToPending={() => setActiveIndex(1)}
                onNavigateToPayments={() => setActiveIndex(2)}
                onNavigateToInvoices={() => setActiveIndex(3)}
              />
            </TabPanel>

            <TabPanel
              header="Members"
              leftIcon="pi pi-users"
            >
              <MemberViewer />
            </TabPanel>

            <TabPanel
              header="Payments"
              leftIcon="pi pi-euro"
            >
              <PaymentDashboard />
            </TabPanel>

            <TabPanel
              header="Invoices"
              leftIcon="pi pi-file-pdf"
            >
              <InvoiceManagement />
            </TabPanel>

            {user?.canManageLogbook && (
              <TabPanel
                header="Logbuch"
                leftIcon="pi pi-book"
              >
                <LogbookManagement />
              </TabPanel>
            )}

            {user?.roles?.includes('ADMIN') && (
              <TabPanel
                header="Admin"
                leftIcon="pi pi-cog"
              >
                <TabView>
                  <TabPanel header="User Management" leftIcon="pi pi-users">
                    <LdapUserManagement />
                  </TabPanel>
                  <TabPanel header="Database Backup" leftIcon="pi pi-database">
                    <DatabaseBackup />
                  </TabPanel>
                </TabView>
              </TabPanel>
            )}
          </TabView>
        </div>
      </main>
    </div>
  )
}

export default Dashboard
